import React, {
  useEffect,
  useMemo,
  useRef,
  FunctionComponent,
  ReactNode,
  useCallback,
  useState,
} from "react";
import { Popup, PopupOptions, PointLike, LngLatLike } from "mapbox-gl";
import ReactDOM from "react-dom";

import useMap from "./use-map";
import useMapboxEvented from "./use-mapbox-evented";
import useDeepCompare from "@alpaca-travel/utils/use-deep-compare";

/**
 * Mounts a Popup onto a map
 *
 * <Map>
 *   <MapPopup lngLat={[50, 50]} maxWidth="50px">
 *    <strong>Hello World</strong>
 *   </MapPopup>
 * </Map>
 *
 * Uses React-DOM to render the children and supplies the HTML to marker with setHTML
 *
 * See the valid props
 *
 * @param {object} props
 */
const MapPopup: FunctionComponent<MapPopupProps> = ({
  anchor,
  offset,
  children,
  lngLat,
  className,
  maxWidth = "240px",
  onClose,
  onOpen,
  closeButton,
  closeOnClick,
  closeOnMove,
  focusAfterOpen = true,
}) => {
  // Obtain the reference to the map
  const { map } = useMap();
  const popup = useRef<Popup>();
  const [latestPopup, setLatestPopup] = useState<Popup>();

  // Render changes into a div (portal)
  const element = useRef(document.createElement("div"));

  // Changes...
  const lngLatLatest = useDeepCompare(lngLat);

  // Detect whether we are rendering children
  const hasChildren = useMemo(() => {
    if (React.Children.count(children) > 0) {
      return true;
    }
  }, [children]);

  // Current Options
  const options = useMemo(() => {
    const newOptions = {
      anchor,
      offset,
      closeButton,
      closeOnClick,
      closeOnMove,
      maxWidth,
      className,
      focusAfterOpen,
      element: hasChildren && element.current,
    };

    return newOptions;
  }, [
    anchor,
    offset,
    closeButton,
    closeOnClick,
    closeOnMove,
    hasChildren,
    className,
    maxWidth,
    focusAfterOpen,
  ]);

  // Creates and replaces the existing popup
  const replacePopup = useCallback(
    (createOptions: PopupOptions, createLngLat: LngLatLike) => {
      if (map) {
        // If we have an existing marker, remove it.
        if (popup.current) {
          popup.current.remove();
        }

        // Instantiate a new one
        const m = new Popup(createOptions);

        m.setLngLat(createLngLat).addTo(map);

        // Support just HTML
        m.setDOMContent(element.current);

        popup.current = m;
        setLatestPopup(m);
      }
    },
    [map, setLatestPopup]
  );

  // Warn about changes to props
  useEffect(() => {
    if (popup.current) {
      console.warn(
        "TODO: Update the options for the popup onto the popup object"
      );
    }
  }, [options]);

  // Create a popup when ready
  useEffect(() => {
    if (map && !popup.current) {
      replacePopup(options, lngLatLatest);
    }
  }, [map, replacePopup, options, lngLatLatest]);

  // On unmount of component, remove from the map
  useEffect(() => {
    return () => {
      if (popup.current) {
        popup.current.remove();
      }
    };
  }, []);

  // Update the lng lat if we have a marker and the lnglat changes
  useEffect(() => {
    if (popup.current) {
      // shift along
      popup.current.setLngLat(lngLatLatest);
    }
  }, [lngLatLatest]);

  // Add event handling
  useMapboxEvented(latestPopup, "close", onClose);
  useMapboxEvented(latestPopup, "open", onOpen);

  // Don't render a portal if there are no children
  if (!hasChildren) {
    return null;
  }

  return ReactDOM.createPortal(children, element.current);
};

export type MapPopupProps = {
  children: ReactNode;
  className?: string;
  lngLat: LngLatLike;

  onClose?: Function;
  onOpen?: Function;

  closeButton?: boolean;
  closeOnClick?: boolean;
  closeOnMove?: boolean;

  color?: string;
  anchor?: string &
    (
      | "center"
      | "top"
      | "bottom"
      | "left"
      | "right"
      | "top-left"
      | "top-right"
      | "bottom-left"
      | "bottom-right"
    );
  offset?: PointLike | {};
  maxWidth?: string;
  focusAfterOpen?: boolean;
};

export default React.memo(MapPopup);
